import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Warning, CircleNotch, Bell, House } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function AlertsUnsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: false, alert: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setState({ loading: false, ok: false, alert: null, error: "Missing token. Please use the link from your email." });
        return;
      }
      try {
        const r = await api.post("/alerts/unsubscribe", { token });
        if (cancelled) return;
        setState({ loading: false, ok: true, alert: r.data.alert || null, error: "" });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false, ok: false, alert: null,
          error: err.response?.data?.detail || "Could not process the request. The link may be invalid.",
        });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="alerts-unsubscribe-page">
      <div className="mc-card w-full max-w-lg p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-[#3B5BDB]/10 text-[#3B5BDB] flex items-center justify-center">
          <Bell size={22} weight="duotone" />
        </div>

        {state.loading && (
          <div className="mt-5">
            <CircleNotch size={20} weight="bold" className="animate-spin mx-auto text-[#3B5BDB]" />
            <p className="mt-3 text-sm text-[#4A5568]">Processing your request…</p>
          </div>
        )}

        {!state.loading && state.ok && (
          <div className="mt-5" data-testid="unsub-success">
            <CheckCircle size={28} weight="duotone" className="mx-auto text-emerald-600" />
            <h1 className="mt-3 font-display text-2xl text-[#0A1128]">You're unsubscribed</h1>
            <p className="mt-2 text-sm text-[#4A5568]">
              {state.alert
                ? <>This price alert has been paused. You won't receive any more emails for{" "}
                    <strong className="text-[#0A1128]">{state.alert.metal?.toUpperCase()} · {state.alert.city}</strong>{" "}
                    at ₹{Number(state.alert.target_price || 0).toLocaleString("en-IN")} ({state.alert.alert_type}).</>
                : "This alert has already been disabled."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <Link
                to="/dashboard?tab=alerts"
                className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth"
                data-testid="manage-alerts-cta"
              >
                Manage all alerts
              </Link>
              <Link
                to="/"
                className="px-4 py-2.5 rounded-md text-[#4A5568] hover:text-[#0A1128] text-sm flex items-center gap-1.5"
              >
                <House size={14} weight="duotone" /> Home
              </Link>
            </div>
          </div>
        )}

        {!state.loading && !state.ok && (
          <div className="mt-5" data-testid="unsub-failure">
            <Warning size={28} weight="duotone" className="mx-auto text-amber-600" />
            <h1 className="mt-3 font-display text-2xl text-[#0A1128]">Couldn't unsubscribe</h1>
            <p className="mt-2 text-sm text-[#4A5568]">{state.error}</p>
            <div className="mt-6">
              <Link
                to="/dashboard?tab=alerts"
                className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth"
              >
                Manage alerts in dashboard
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
